import { axiosInstance } from "./apiClient";

export interface CatalogPreviewItem {
  /** True for chapter/section header rows (shown for debug, never imported). */
  is_section: boolean;
  numero: string;
  codice: string | null;
  descrizione_breve: string | null;
  descrizione: string;
  udm: string | null;
  qta: number | null;
  prezzo: number | null;
  costo_mat_unitario: number | null;
  tempo_posa_ore: number | null;
  costo_mano_unitario: number | null;
  categoria_merceologica: string | null;
  section_path: string[];
}

export interface CatalogSourceCheckResult {
  exists: boolean;
  source_file: string;
  item_count: number;
  last_imported_at: string | null;
  categoria: string | null;
  disciplina: string | null;
  anno: number | null;
}

export interface CatalogSourceEntry {
  source_file: string;
  categoria: string | null;
  disciplina: string | null;
  anno: number | null;
  item_count: number;
  last_imported_at: string | null;
}

export interface CatalogSourceDeleteResult {
  source_file: string;
  deleted: number;
  index_deleted: number;
  referenced_by_rows: number;
}

export interface CatalogImportPreviewResult {
  layout: string;
  header_row: number;
  total_items: number;
  total_sections: number;
  items_with_costs: number;
  items_missing_costs: number;
  warnings: string[];
  prefill: {
    categoria?: string;
    disciplina?: string;
    anno?: number;
  };
  preview_items: CatalogPreviewItem[];
}

export interface CatalogImportJobStatus {
  job_id: string;
  /** pending | processing | done | failed */
  status: "pending" | "processing" | "done" | "failed";
  total: number;
  processed: number;
  created: number;
  updated: number;
  skipped: number;
  warnings: string[];
  error: string | null;
  elapsed_seconds: number | null;
}

/** Legacy sync commit response (kept for backward compat) */
export interface CatalogImportCommitResult {
  created: number;
  updated: number;
  skipped: number;
  warnings: string[];
  created_uids: string[];
}

export const catalogImportApi = {
  listSources: async (): Promise<CatalogSourceEntry[]> => {
    const res = await axiosInstance.get("/catalog/import/sources");
    return res.data;
  },

  /** Irreversibly deletes every catalog item imported from the given sources-table row. */
  deleteSource: async (source: Pick<CatalogSourceEntry, "source_file" | "categoria" | "disciplina" | "anno">): Promise<CatalogSourceDeleteResult> => {
    const res = await axiosInstance.post(
      "/catalog/import/sources/delete",
      {
        source_file: source.source_file,
        categoria: source.categoria,
        disciplina: source.disciplina,
        anno: source.anno,
      },
      { timeout: 120_000 },
    );
    return res.data;
  },

  checkSource: async (filename: string): Promise<CatalogSourceCheckResult> => {
    const res = await axiosInstance.get("/catalog/import/check-source", {
      params: { filename },
    });
    return res.data;
  },

  preview: async (file: File): Promise<CatalogImportPreviewResult> => {
    const form = new FormData();
    form.append("file", file);
    const res = await axiosInstance.post("/catalog/import/preview", form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60_000,
    });
    return res.data;
  },

  /**
   * Start a background import job.  Returns immediately with the job_id.
   * Poll `getJob(job_id)` until status === "done" | "failed".
   */
  start: async (
    file: File,
    params: {
      categoria?: string;
      disciplina?: string;
      anno?: number;
      /** Posizioni (campo `numero`) delle righe eliminate in anteprima */
      excludedPositions?: string[];
    }
  ): Promise<{ job_id: string; total_items: number }> => {
    const form = new FormData();
    form.append("file", file);
    if (params.categoria) form.append("categoria", params.categoria);
    if (params.disciplina) form.append("disciplina", params.disciplina);
    if (params.anno != null) form.append("anno", String(params.anno));
    if (params.excludedPositions && params.excludedPositions.length > 0) {
      form.append("excluded_positions_json", JSON.stringify(params.excludedPositions));
    }
    const res = await axiosInstance.post("/catalog/import/start", form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60_000,
    });
    return res.data;
  },

  getJob: async (jobId: string): Promise<CatalogImportJobStatus> => {
    const res = await axiosInstance.get(`/catalog/import/jobs/${jobId}`, {
      timeout: 30_000,
    });
    return res.data;
  },

  /** Legacy synchronous commit — kept for backward compat, use start() instead */
  commit: async (
    file: File,
    params: { categoria?: string; disciplina?: string; anno?: number }
  ): Promise<CatalogImportCommitResult> => {
    const form = new FormData();
    form.append("file", file);
    if (params.categoria) form.append("categoria", params.categoria);
    if (params.disciplina) form.append("disciplina", params.disciplina);
    if (params.anno != null) form.append("anno", String(params.anno));
    const res = await axiosInstance.post("/catalog/import/commit", form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120_000,
    });
    return res.data;
  },
};
